/**
 * Client for the unified multiplex WebSocket API (channel "multiplex").
 * Follows the same protocol as orchestrator E2E stream tests and router/websocket/multiplex.
 *
 * Flow: connect → subscribe to channel "multiplex" → send action "subscribe"/"unsubscribe"/"ping"
 * with subscriptions array → receive ack/data/error/pong with stream_type for data.
 */

import { WebSocketClient } from "../../transport/websocket/WebSocketClient";
import type {
  MultiplexStreamType,
  MultiplexStreamSubscription,
  MultiplexResponse,
  MultiplexDataCallback,
} from "../../types/api";
import { decodeStreamPayload } from "../../utils/streamPayload";
import { normalizeSnakeToCamel } from "../../utils/normalize";

const MULTIPLEX_CHANNEL = "multiplex";

/** Router and SDK both use camelCase streamType (e.g. priceFeed). Pass-through. */
function streamTypeFromWire(wire: string): MultiplexStreamType {
  return wire as MultiplexStreamType;
}

const INIT_MULTIPLEX_ID = "init-multiplex";

type Pending = { resolve: () => void; reject: (err: Error) => void };

export class MultiplexStreamClient {
  private wsClient: WebSocketClient;
  private dataCallbacks: Map<MultiplexStreamType, MultiplexDataCallback[]> =
    new Map();
  private pendingById: Map<string, Pending> = new Map();
  private pingPendingById: Map<string, Pending> = new Map();
  private wildcardHandler: ((data: Record<string, unknown>) => void) | null = null;
  private channelSubscribed = false;

  constructor(wsClient: WebSocketClient) {
    this.wsClient = wsClient;
  }

  /**
   * Subscribe to the multiplex channel (step 2 in E2E flow).
   * Call after connect(). Resolves when server acks the channel subscription.
   */
  async subscribeToChannel(timeoutMs: number = 5000): Promise<void> {
    if (!this.wsClient.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }
    if (this.channelSubscribed) {
      return;
    }

    this.wsClient.registerPendingSubscription(MULTIPLEX_CHANNEL);
    this.wsClient.send({
      type: "subscribe",
      channel: MULTIPLEX_CHANNEL,
      id: INIT_MULTIPLEX_ID,
    });
    await this.wsClient.waitForSubscription(MULTIPLEX_CHANNEL, timeoutMs);
    this.channelSubscribed = true;
    this.attachMessageHandler();
  }

  /**
   * Subscribe to one or more streams (multiplex protocol).
   * Resolves when server acks the subscribe request.
   */
  async subscribeStreams(
    subscriptions: MultiplexStreamSubscription[],
    timeoutMs: number = 5000,
  ): Promise<void> {
    await this.ensureChannelAndHandler();
    const id = `sub-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => {
        const p = this.pendingById.get(id);
        if (p && this.pendingById.delete(id)) {
          p.reject(new Error(`Multiplex subscribe timeout (id: ${id})`));
        }
      }, timeoutMs);
      const resolveOnce = () => {
        clearTimeout(t);
        if (this.pendingById.delete(id)) resolve();
      };
      const rejectOnce = (err: Error) => {
        clearTimeout(t);
        if (this.pendingById.delete(id)) reject(err);
      };
      this.pendingById.set(id, { resolve: resolveOnce, reject: rejectOnce });

      const wireSubs = subscriptions.map((s) => ({
        streamType: s.stream_type,
        filter: s.filter,
      }));
      this.wsClient.send({
        action: "subscribe",
        id,
        subscriptions: wireSubs,
      });
    });
  }

  /**
   * Unsubscribe from one or more streams.
   * Does not wait for ack; use subscribeStreams pattern if you need to wait.
   */
  unsubscribeStreams(subscriptions: MultiplexStreamSubscription[]): void {
    if (!this.channelSubscribed || !this.wsClient.isConnected()) {
      return;
    }
    const id = `unsub-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const wireSubs = subscriptions.map((s) => ({
      streamType: s.stream_type,
      filter: s.filter,
    }));
    this.wsClient.send({
      action: "unsubscribe",
      id,
      subscriptions: wireSubs,
    });
  }

  /**
   * Register a callback for a specific stream type (data messages only).
   * Call before or after subscribeStreams; callbacks are invoked for type "data" with matching stream_type.
   */
  onStreamData(
    streamType: MultiplexStreamType,
    callback: MultiplexDataCallback,
  ): () => void {
    if (!this.dataCallbacks.has(streamType)) {
      this.dataCallbacks.set(streamType, []);
    }
    this.dataCallbacks.get(streamType)!.push(callback);
    return () => {
      const list = this.dataCallbacks.get(streamType);
      if (list) {
        const i = list.indexOf(callback);
        if (i !== -1) list.splice(i, 1);
      }
    };
  }

  /**
   * Ping (keepalive). Resolves when pong is received.
   */
  async ping(timeoutMs: number = 5000): Promise<void> {
    await this.ensureChannelAndHandler();
    const id = `ping-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => {
        if (this.pingPendingById.delete(id)) {
          reject(new Error(`Multiplex pong timeout (id: ${id})`));
        }
      }, timeoutMs);
      this.pingPendingById.set(id, {
        resolve: () => {
          clearTimeout(t);
          this.pingPendingById.delete(id);
          resolve();
        },
        reject: (err) => {
          clearTimeout(t);
          this.pingPendingById.delete(id);
          reject(err);
        },
      });
      this.wsClient.send({
        action: "ping",
        id,
      });
    });
  }

  /**
   * Detach handler and clear channel state. Call on disconnect if reusing the same WebSocketClient.
   */
  detach(): void {
    if (this.wildcardHandler) {
      this.wsClient.off("*", this.wildcardHandler);
      this.wildcardHandler = null;
    }
    this.channelSubscribed = false;
    this.dataCallbacks.clear();
    this.pendingById.clear();
    this.pingPendingById.clear();
  }

  private async ensureChannelAndHandler(): Promise<void> {
    if (!this.channelSubscribed) {
      await this.subscribeToChannel();
    }
  }

  private attachMessageHandler(): void {
    if (this.wildcardHandler) return;
    this.wildcardHandler = (data: Record<string, unknown>) => {
      const type = data.type as string | undefined;
      const id = data.id as string | undefined;
      const streamTypeWire = (data.stream_type ??
        data.streamType ??
        data.subChannel) as string | undefined;
      const streamType = streamTypeWire
        ? streamTypeFromWire(streamTypeWire)
        : undefined;

      // Multiplex data: type "data" and stream_type/streamType/subChannel set (router may send streamType or subChannel)
      if (type === "data" && streamType) {
        const callbacks = this.dataCallbacks.get(streamType);
        if (callbacks?.length) {
          const rawData = data.data;
          let dataWithDecoded: unknown =
            rawData != null && typeof rawData === "object"
              ? decodeStreamPayload(rawData)
              : rawData;
          const envelope = dataWithDecoded as {
            decodedPayload?: unknown;
            decodedPayloadRaw?: Uint8Array;
          };
          if (envelope?.decodedPayload != null) {
            dataWithDecoded = envelope.decodedPayload;
          } else if (
            dataWithDecoded != null &&
            typeof dataWithDecoded === "object" &&
            !("decodedPayload" in (dataWithDecoded as object)) &&
            !("payload" in (dataWithDecoded as object))
          ) {
            dataWithDecoded = normalizeSnakeToCamel(
              dataWithDecoded as Record<string, unknown>,
            );
          }
          const respForCallback: MultiplexResponse = {
            type: "data",
            id: data.id as string | undefined,
            stream_type: streamType,
            symbol: data.symbol as string | undefined,
            marketIndex: data.marketIndex as number | undefined,
            data: dataWithDecoded,
            error: data.error as MultiplexResponse["error"],
            U: data.U as number | undefined,
            u: data.u as number | undefined,
            ts: data.ts as number | undefined,
          };
          callbacks.forEach((cb) => {
            try {
              cb(respForCallback);
            } catch (e) {
              console.error(
                `[MultiplexStreamClient] callback error for ${streamType}:`,
                e,
              );
            }
          });
        }
        return;
      }

      // Ack for our subscribe/unsubscribe request (by id)
      if (type === "ack" && id) {
        const pending = this.pendingById.get(id);
        if (pending) {
          pending.resolve();
        }
        return;
      }

      // Error for our request (by id)
      if (type === "error" && id) {
        const pending = this.pendingById.get(id);
        if (pending) {
          const err = data.error;
          let message = "Multiplex error";
          if (err != null && typeof err === "object") {
            const o = err as Record<string, unknown>;
            if (typeof o.message === "string") message = o.message;
            else if (typeof o.code === "string") message = o.code;
          }
          pending.reject(new Error(message));
        }
        return;
      }

      // Pong for our ping (by id)
      if (type === "pong" && id) {
        const pending = this.pingPendingById.get(id);
        if (pending) {
          pending.resolve();
        }
      }
    };
    this.wsClient.on("*", this.wildcardHandler);
  }
}
